import React, { useState, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import bg from "../../assets/background.svg";

import useUrlFilters from "../../hooks/pearli/useUrlFilters";
import usePearliScores from "../../hooks/pearli/usePearliScores";
import usePearliRange from "../../hooks/pearli/usePearliRange";
import usePearliAiSummary from "../../hooks/pearli/usePearliAiSummary";

import {
    getCurrentMonthRange,
    getDefaultRangeMonthKeys,
    getRecentMonthOptions,
    getValidatedMonthRange,
    monthKeyToRange,
    monthKeyToLabel,
} from "../../utils/dateUtils";

import DateControls from "../../components/common/date-controls/DateControls";
import FiltersPanel from "../../components/pearli/FiltersPanel";
import RadarChart from "../../components/pearli/RadarChart";
import ScoreSummary from "../../components/pearli/ScoreSummary";
import AISummary from "../../components/pearli/AISummary";
import MonthRangeSelector from "../../components/pearli/MonthRangeSelector";
import PearliRangeComparison from "../../components/pearli/PearliRangeComparison";
import ParticipantStatsCard from "../../components/pearli/ParticipantStatsCard";
import UsersRangeCard from "../../components/pearli/UsersRangeCard";

import { useDispatch } from "react-redux";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Fab, Tooltip } from "@mui/material";
import { logout } from "../../store/authSlice";
import { useNavigate } from "react-router-dom";

export default function PearliAssessment() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { params, setParams, viewMode, filters, setFilter, clearFilters } =
        useUrlFilters();

    const [filtersOpen, setFiltersOpen] = useState(false);

    const { start: currentStart, end: currentEnd } = getCurrentMonthRange();
    const { startKey: defaultStart, endKey: defaultEnd } = getDefaultRangeMonthKeys();
    const monthOptions = useMemo(() => getRecentMonthOptions(), []);

    const handleBack = () => {
        navigate("/Home");
    };

    const rawStartKey = params.get("range_start_month") || defaultStart;
    const rawEndKey = params.get("range_end_month") || defaultEnd;
    const { startKey, endKey } = useMemo(
        () =>
            getValidatedMonthRange({
                startKey: rawStartKey,
                endKey: rawEndKey,
            }),
        [rawEndKey, rawStartKey]
    );

    const buildUrl = useCallback(
        (s, e) => {
            const p = new URLSearchParams();
            p.set("start_date", s);
            p.set("end_date", e);

            Object.entries(filters).forEach(([k, v]) => {
                if (v) p.set(k.replace(/([A-Z])/g, "_$1").toLowerCase(), v);
            });

            return `/api/pearli/average?${p.toString()}`;
        },
        [
            filters.age,
            filters.gender,
            filters.location,
            filters.description,
            filters.jobTitle,
            filters.company,
            filters.interest,
        ]
    );

    // Build current view URL
    const currentUrl = buildUrl(currentStart, currentEnd);

    const {
        scores,
        filteredCount: currentFilteredCount,
        totalCount: currentTotalCount,
        loading: loadingScores,
        hasData,
    } = usePearliScores(viewMode, currentUrl);


    const {
        prevScores,
        focusScores,
        prevCount,
        focusCount,
        prevLabel,
        focusLabel,
        loading: loadingRange,
        hasPrevData,
        hasFocusData,
    } = usePearliRange(viewMode, startKey, endKey, buildUrl);

    const { summaries, aiSummary, prevSummary, loading: loadingAi } =
        usePearliAiSummary(viewMode, scores, loadingScores);

    const displayDateRange = useMemo(() => {
        if (viewMode === "current") {
            return `${currentStart} → ${currentEnd}`;
        }
        return `${monthKeyToLabel(startKey)} – ${monthKeyToLabel(endKey)}`;
    }, [viewMode, startKey, endKey, currentStart, currentEnd]);

    // Placeholder for new endpoint
    const totalUsers = null;

    const filteredUsersForView =
        viewMode === "current" ? currentFilteredCount : focusCount;

    const participantsLoading =
        viewMode === "current" ? loadingScores : loadingRange;

    return (
        <Box
            sx={{
                display: "block",
                minHeight: "100vh",
                width: "100%",
                backgroundColor: "#F8FAFC",
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                position: "relative",
            }}
        >
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>

                {/* Top Controls */}
                <DateControls
                    viewMode={viewMode}
                    displayDateRange={displayDateRange}
                    onChangeView={(v) => {
                        const p = new URLSearchParams(params);
                        p.set("view", v);
                        setParams(p);
                    }}
                    onToggleFilters={() => setFiltersOpen(!filtersOpen)}
                />

                {filtersOpen && (
                    <FiltersPanel
                        values={filters}
                        onChange={setFilter}
                        onClear={clearFilters}
                    />
                )}

                {/* RANGE MODE: Month selector */}
                {viewMode === "range" && (
                    <MonthRangeSelector
                        startMonth={startKey}
                        endMonth={endKey}
                        monthOptions={monthOptions}
                        onChangeStart={(v) => {
                            const p = new URLSearchParams(params);
                            p.set("range_start_month", v);
                            setParams(p);
                        }}
                        onChangeEnd={(v) => {
                            const p = new URLSearchParams(params);
                            p.set("range_end_month", v);
                            setParams(p);
                        }}
                    />
                )}

                {/* CURRENT MODE */}
                {viewMode === "current" ? (
                    <>
                        <ParticipantStatsCard
                            totalUsers={currentTotalCount}
                            filteredUsers={currentFilteredCount}
                            loading={loadingScores}
                            hasData={hasData}
                        />

                        <RadarChart scores={scores} loading={loadingScores} hasData={hasData} />

                        <ScoreSummary
                            scores={scores}
                            summaries={summaries}
                            loading={loadingScores}
                            hasData={hasData}
                        />

                        <AISummary
                            summary={aiSummary || prevSummary}
                            isRefreshing={loadingAi}
                            hasData={hasData}
                        />
                    </>
                ) : (
                    <>
                        {/* RANGE USER COUNT CARD */}
                        <UsersRangeCard
                            prevCount={prevCount}
                            focusCount={focusCount}
                            prevLabel={prevLabel}
                            focusLabel={focusLabel}
                            loading={loadingRange}
                            hasPrevData={hasPrevData}
                            hasFocusData={hasFocusData}
                        />

                        {/* RANGE SCORES COMPARISON */}
                        <PearliRangeComparison
                            prevScores={prevScores}
                            focusScores={focusScores}
                            prevMonth={monthKeyToRange(startKey)}
                            focusMonth={monthKeyToRange(endKey)}
                            loading={loadingRange}
                            hasPrevData={hasPrevData}
                            hasFocusData={hasFocusData}
                        />
                    </>
                )}
            </Box>

            {/* Floating Back Button */}
            <Tooltip title="Go Back" placement="left">
                <Fab
                    onClick={handleBack}
                    color="primary"
                    size="small"
                    sx={{
                        position: "fixed",
                        bottom: 100,
                        right: 24,
                        zIndex: 2000,
                        width: 56,
                        height: 56,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    }}
                >
                    <ArrowBackIosNewIcon />
                </Fab>
            </Tooltip>
        </Box>
    );
}
